import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { EMPTY, Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';

// ─── Enums (mirrors backend) ──────────────────────────────────────────────────

export type BlogStatus   = 'DRAFT' | 'PUBLISHED';
export type BlogBlockType = 'TEXT' | 'IMAGE' | 'VIDEO';
export type PostAudienceType =
  | 'EVERYONE'
  | 'FOLLOWERS_FRIENDS'
  | 'FRIENDS_ONLY'
  | 'ONLY_ME';
export type ReactionType = 'LIKE' | 'HEART' | 'HUG' | 'WOW' | 'SAD' | 'HAHA';

// ─── Shared interfaces ────────────────────────────────────────────────────────

export interface IMediaItem {
  _id:      string;
  url:      string;
  urlSmall: string;
  urlMedium: string;
  urlLarge: string;
  urlBlob?: string;
  type:     'image' | 'video';
}

export interface IUserSnippet {
  _id:          string;
  displayName:  string;
  userName:     string;
  profileImage: { urlSmall?: string; urlMedium?: string; urlLarge?: string } | null;
  status?:      string;
}

export interface IReactionSummary {
  total:      number;
  counts:     Record<ReactionType, number>;
  myReaction: ReactionType | null;

  recentUserName: string | null;
  topTypes: string[];
}

// ─── Blog-specific interfaces ─────────────────────────────────────────────────

/**
 * A single resolved blog block as returned by the API.
 * TEXT blocks carry `text`; IMAGE/VIDEO blocks carry a populated `mediaId`.
 */
export interface IBlogBlock {
  position: number;
  type:     BlogBlockType;
  text?:    string;
  mediaId?: IMediaItem | null;   // populated Media document
}

export interface IBlogCollaboration {
  user: IUserSnippet;
  status: 'PENDING' | 'ACCEPTED' | 'DECLINED';
  invitedAt: string;
  respondedAt?: string;
}

export interface IBlogInfo {
  _id:            string;
  status:         BlogStatus;
  audience:       PostAudienceType;
  blocks:         IBlogBlock[];
  coverImage?:    IMediaItem | null;
  readTime:       number;
  commentsDisabled?: boolean;
  mentions:       IUserSnippet[];
  collaborations: IBlogCollaboration[];
}

export interface IBlog {
  _id:              string;
  title:            string;
  type:             'BLOG';
  user:             IUserSnippet;
  blogInfo:         IBlogInfo | null;
  hashtags:         string[];
  numberOfLikes:    number;
  numberOfComments: number;
  numberOfShares:   number;
  engagementScore:  number;
  reactions:        IReactionSummary;
  isMyBlog:         boolean;
  isFollowed:       boolean;
  friendship:       { status: string; requestId?: string };
  createdAt:        string;
  updatedAt:        string;
}

export interface IPaginatedBlogs {
  items: IBlog[];
  total: number;
  page:  number;
  limit: number;
}

// ─── Incoming block shape used when building payloads ────────────────────────

/**
 * What the frontend sends for each block before upload.
/**
 * TEXT blocks: provide `text`.
 * IMAGE/VIDEO blocks: provide `file` (new upload) OR `existingMediaId` (already on server).
 */
export interface IBlockInput {
  type:      BlogBlockType;
  position:  number;
  text?:     string;   // TEXT blocks only
  file?:     File;     // IMAGE / VIDEO blocks only — new upload, not sent in JSON
  existingMediaId?: string;  // IMAGE / VIDEO blocks already on server — sent in JSON, no file needed
}

// ─── Create / Update payloads ─────────────────────────────────────────────────

export interface ICreateBlogPayload {
  title:            string;
  blocks:           IBlockInput[];
  hashtags?:        string;
  audience?:        PostAudienceType;
  status?:          BlogStatus;
  mentions?:        string[];   // User ObjectId strings
  collaboratorIds?: string[];   // User ObjectId strings
  commentsDisabled?: boolean;
}

export interface IUpdateBlogPayload {
  title?:           string;
  blocks?:          IBlockInput[];
  hashtags?:        string;
  audience?:        PostAudienceType;
  status?:          BlogStatus;
  mentions?:        string[];
  collaboratorIds?: string[];
  commentsDisabled?: boolean;
}

// ─── Service ──────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class BlogService {

  private readonly http       = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly baseUrl    = `${environment.apiUrl}`;

  // Subject to notify other components when a blog is created/updated/deleted
  private readonly refresh$ = new Subject<void>();
  readonly onRefresh$ = this.refresh$.asObservable();

  triggerRefresh(): void {
    this.refresh$.next();
  }

  // ── Create ─────────────────────────────────────────────────────────────────

  /**
   * Creates a new blog.
   *
   * How blocks are sent:
   *   1. The `blocks` array is serialised to JSON and appended as a string field
   *      named "blocks" — without the `file` property (files go separately).
   *   2. Every IMAGE/VIDEO block's file is appended under the field name "files"
   *      IN THE SAME ORDER as those blocks appear sorted by position.
   *      The backend matches files[0] → first media block, files[1] → second, etc.
   *
   * Example body (multipart/form-data):
   *   title    = "My Blog"
   *   blocks   = '[{"type":"TEXT","position":0,"text":"Hello"},{"type":"IMAGE","position":1}]'
   *   files[0] = <image file>
   *   status   = "DRAFT"
   */
  createBlog(payload: ICreateBlogPayload): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.post(`${this.baseUrl}/blogs`, this.buildFormData(payload));
  }

  // ── Update ─────────────────────────────────────────────────────────────────

  /**
   * Updates an existing blog (owner only).
   * Pass `status: 'PUBLISHED'` to publish a draft.
   * When `blocks` is provided the ENTIRE blocks array is replaced.
   */
  updateBlog(blogId: string, payload: IUpdateBlogPayload): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.patch(`${this.baseUrl}/blogs/${blogId}`, this.buildFormData(payload));
  }

  // ── Read ───────────────────────────────────────────────────────────────────

  /**
   * GET /api/v1/blogs/:blogId — single blog. Drafts return 404 for non-owners.
   * Runs during SSR too (no isPlatformBrowser guard) — the blog/:id route is
   * RenderMode.Server specifically so this data (and the SeoService tags built
   * from it) is present in the initial HTML for share-preview crawlers.
   */
  getBlog(blogId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/blogs/${blogId}`);
  }

  /** GET /api/v1/blogs?page=&limit= — paginated published feed. */
  getBlogs(page = 1, limit = 10): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    const params = new HttpParams()
      .set('page',  String(page))
      .set('limit', String(limit));
    return this.http.get(`${this.baseUrl}/blogs`, { params });
  }

  getFeaturedBlogs(): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.get(`${this.baseUrl}/blogs/featured`);
  }

  /** GET /api/v1/blogs/user/:userName — all blogs for a profile page. */
  getUserBlogs(userName: string, page = 1, limit = 10, collabOnly = false): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    let params = new HttpParams()
      .set('page',  String(page))
      .set('limit', String(limit));
    if (collabOnly) params = params.set('collabOnly', 'true');
    return this.http.get(`${this.baseUrl}/blogs/user/${userName}`, { params });
  }

  // ── Delete ─────────────────────────────────────────────────────────────────

  /** DELETE /api/v1/blogs/:blogId — owner only. */
  deleteBlog(blogId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.delete(`${this.baseUrl}/blogs/${blogId}`);
  }

  /** PATCH /api/v1/blogs/:blogId/toggle-comments — owner only. */
  toggleComments(blogId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.patch(`${this.baseUrl}/blogs/${blogId}/toggle-comments`, {});
  }

  // ── Reactions (reuse existing reactions endpoint) ─────────────────────────

  /**
   * React to a blog (same endpoint as posts — content-agnostic).
   * Toggling the same type removes the reaction.
   */
  reactToBlog(blogId: string, type: ReactionType): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.post(`${this.baseUrl}/reactions/${blogId}`, { type });
  }

  /** Get all reactions for a blog, optionally filtered by type. */
  getReactions(blogId: string, type?: ReactionType): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    let params = new HttpParams();
    if (type) params = params.set('type', type);
    return this.http.get(`${this.baseUrl}/reactions/${blogId}`, { params });
  }

  // ── Publish helper ────────────────────────────────────────────────────────

  /**
   * Convenience: publish a draft blog without changing any other fields.
   * Calls updateBlog with only `status: 'PUBLISHED'`.
   */
  publishBlog(blogId: string): Observable<any> {
    return this.updateBlog(blogId, { status: 'PUBLISHED' });
  }

  /**
   * Convenience: unpublish a blog back to draft.
   */
  unpublishBlog(blogId: string): Observable<any> {
    return this.updateBlog(blogId, { status: 'DRAFT' });
  }

  // ── Collaboration ────────────────────────────────────────────────────────

  acceptCollaboration(blogId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.patch(`${this.baseUrl}/blogs/${blogId}/collaborate/accept`, {});
  }

  declineCollaboration(blogId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.patch(`${this.baseUrl}/blogs/${blogId}/collaborate/decline`, {});
  }

  /** DELETE /api/v1/blogs/:blogId/collaborate/leave — collaborator removes themselves. */
  leaveCollaboration(blogId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.delete(`${this.baseUrl}/blogs/${blogId}/collaborate/leave`);
  }

  /** GET /api/v1/blogs/:blogId/recommendations — returns up to 3 recommended blogs. */
  getRecommendations(blogId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.get(`${this.baseUrl}/blogs/${blogId}/recommendations`);
  }

  // ── Private: FormData builder ─────────────────────────────────────────────

  /**
   * Builds a FormData object from a create or update payload.
   *
   * Block serialisation:
   *   - Strip the `file` property from each block before JSON-stringifying,
   *     since files are appended separately as binary parts.
   *   - Collect all IMAGE/VIDEO block files sorted by block position
   *     and append them in that order under the field name "files".
   *
   * This matches exactly what the backend's uploadMediaBlocks() expects:
   *   media blocks sorted by position → files[0], files[1], files[2] …
   */
  private buildFormData(
    payload: ICreateBlogPayload | IUpdateBlogPayload,
  ): FormData {
    const fd = new FormData();

    // ── Title ────────────────────────────────────────────────────────────────
    if (payload.title !== undefined) {
      fd.append('title', payload.title);
    }

    // ── Blocks ───────────────────────────────────────────────────────────────
    if (payload.blocks !== undefined) {
      // Sort media blocks by position so file order aligns with block order
      const mediaBlocks = payload.blocks
        .filter(b => b.type !== 'TEXT')
        .sort((a, b) => a.position - b.position);

      // Build the JSON-safe blocks array (no `file` property)
      const blocksForJson = payload.blocks.map(({ file: _file, ...rest }) => rest);
      fd.append('blocks', JSON.stringify(blocksForJson));

      // Append files in the same order as sorted media blocks
      for (const block of mediaBlocks) {
        if (block.file) {
          fd.append('files', block.file);
        }
      }
    }

    // ── Optional scalar fields ────────────────────────────────────────────────
    if (payload.hashtags !== undefined) fd.append('hashtags', payload.hashtags);
    if (payload.audience !== undefined) fd.append('audience', payload.audience);
    if (payload.status   !== undefined) fd.append('status',   payload.status);

    // ── Mentions and collaborators ────────────────────────────────────────────
    // Send whenever the field was provided at all, even as an empty array —
    // an empty array is how "I removed every tag/collaborator" is
    // communicated. Guarding on `.length` would drop that field entirely
    // when it goes to zero, so the backend never learns it should clear the
    // existing list, and the last tag/collaborator sticks around forever.
    if (payload.mentions !== undefined) {
      fd.append('mentions', payload.mentions.join(','));
    }
    if (payload.collaboratorIds !== undefined) {
      fd.append('collaboratorIds', payload.collaboratorIds.join(','));
    }
    if ((payload as ICreateBlogPayload).commentsDisabled) {
      fd.append('commentsDisabled', 'true');
    }

    return fd;
  }
}